import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getDocs, sum, Timestamp } from "firebase/firestore";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Label } from "recharts";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Box, Typography, Card, CardContent, CircularProgress } from "@mui/material";
import { MessageDocument, useFirebaseService } from "../../../data/FirebaseService";
import MessageSummaryChart from "./ResultSummaryChart";
import { format } from "date-fns";

interface PersonStatus {
    id: string;
    name: string;
    status: string;
    date: Timestamp | undefined;
}

interface MessageResult {
    name: string;
    events: { type: string, timestamp: Timestamp }[];
}

const MessageResultsPage = () => {
    const { messageId } = useParams<{ messageId: string }>();
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState({ sent: 0, read: 0, ignored: 0 });
    const [peopleData, setPeopleData] = useState<PersonStatus[]>([]);
    const firebaseService = useFirebaseService();

    useEffect(() => {
        if (!messageId || !firebaseService) return;

        const fetchMessageResults = async () => {
            setLoading(true);
            try {
                const message = (await firebaseService.getDocument<MessageDocument>("messages", messageId));
                if (!message) {
                    console.error("Message not found");
                    return;
                }

                const resultsRef = firebaseService.getCollection(`messages/${messageId}/results`);
                const resultsSnapshot = await getDocs(resultsRef);

                const messageStatuses: Record<string, string> = {
                    read: "Read",
                    sent: "Sent",
                    ignored: "Ignored",
                };

                const peopleList: PersonStatus[] = resultsSnapshot.docs.map((result: any) => {
                    const data = result.data() as MessageResult;
                    const lastEvent = data.events.length > 0 ? data.events[data.events.length - 1] : null;
                    const status: PersonStatus = {
                        id: result.id,
                        name: data.name,
                        status: messageStatuses[lastEvent?.type ?? ""] ?? lastEvent?.type ?? "Unknown",
                        date: lastEvent?.timestamp,
                    };
                    return status;
                });

                setPeopleData(peopleList);

                const sent = message.sentTo?.total ?? 0;
                const read = peopleList.filter((person) => person.status === "Read").length;
                const ignored = Math.max(0, sent - read);
                setSummary({ sent, read, ignored });

            } catch (error) {
                console.error("Error fetching message data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchMessageResults();
    }, [firebaseService, messageId]);

    const columns: GridColDef[] = [
        { field: "name", headerName: "Name", width: 200 },
        { field: "status", headerName: "Status", width: 120 },
        {
            field: "date", headerName: "Date", width: 180, valueFormatter: (params: Timestamp) => {
                if (params) {
                    const date = params.toDate();
                    return format(date, "MMM dd, yyyy hh:mm a"); // Format it
                }
            }
        },
    ];

    return (
        <div>
            <Typography variant="h5" component="h2" className='page-title'>Message Results</Typography>

            {loading ? (
                <CircularProgress />
            ) : (
                <>
                    {/* Chart Summary */}
                    <MessageSummaryChart sent={summary.sent} read={summary.read} ignored={summary.ignored} />

                    {/* DataGrid for Details */}
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Detailed Responses
                            </Typography>
                            <DataGrid rows={peopleData} columns={columns} autoHeight pageSizeOptions={[5, 10]} />
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
};

export default MessageResultsPage;
